/**
 * 약관 동의 서비스
 * - terms 컬렉션의 약관 본문/버전을 기준으로 사용자의 동의 상태를 계산
 * - 동의 1건 = terms_agreements_log 1문서 (약관별로 분리, 본문 스냅샷 포함)
 * - users/{uid} 에 동의 완료 시각 + 동의한 약관 키/버전 머지 저장
 */
import {
  collection, doc, getDocs, query, orderBy, limit as fsLimit,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { getAllTerms, TERMS_TYPES } from './termsService';

const LOG_COL = 'terms_agreements_log';
const PROJECT_ID = process.env.REACT_APP_ENV === 'staging' ? 'test-83e89' : 'ieum-8fd99';
const CF_BASE = `https://us-central1-${PROJECT_ID}.cloudfunctions.net`;
const PATH_KEY = 'ieum.consent_path';

// 가입 진입점별 동의경로 — 클라 시안 매핑과 1:1
export const CONSENT_PATHS = {
  oauth: 'oauth-consent',
  landing_signup_partner: 'landing_signup_partner',
  landing_signup_realtor: 'landing_signup_realtor',
  signup: 'signup',
  consent_gate: 'consent-gate',
  admin: 'admin',
};

export const setConsentPath = (path) => {
  try { sessionStorage.setItem(PATH_KEY, path); } catch {}
};

export const consumeConsentPath = () => {
  try {
    const v = sessionStorage.getItem(PATH_KEY) || '';
    if (v) sessionStorage.removeItem(PATH_KEY);
    return v;
  } catch { return ''; }
};

const ROLE_REQUIRED_KEYS = {
  partner: ['service', 'privacy', 'partner_contract', 'purchase_refund', 'operation_policy'],
  realtor: ['service', 'privacy', 'realtor_consent'],
  admin: [],
  staff: [],
};

const TERMS_LABEL_BY_KEY = TERMS_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});

export const getRoleRequiredKeys = (role) => ROLE_REQUIRED_KEYS[role] || [];

/**
 * 사용자 프로필에서 동의 상태 필드를 안전하게 읽음
 * - 신규 회원가입 직후엔 없을 수 있음
 */
const readConsentStateFromProfile = (profile) => {
  if (!profile) return { consentedVersions: {}, consentCompletedAt: '' };
  return {
    consentedVersions: profile.consentedTermVersions || {},
    consentCompletedAt: profile.consentCompletedAt || '',
  };
};

/**
 * 게이트 상태 계산
 *  - requiredTerms: 역할에 필요한 약관 객체 배열
 *  - missingRequiredTerms: 동의 기록 없거나 버전 불일치인 약관
 *  - requiresConsent: missingRequiredTerms.length > 0
 */
export const getConsentGateState = async ({ profile, role }) => {
  const requiredKeys = getRoleRequiredKeys(role);
  if (requiredKeys.length === 0) {
    return {
      requiredTerms: [],
      missingRequiredTerms: [],
      requiredConsentKeys: [],
      requiresConsent: false,
      consentCompletedAt: '',
    };
  }

  const termsByKey = await getAllTerms();
  const requiredTerms = requiredKeys
    .map((key) => termsByKey[key])
    .filter(Boolean)
    .map((t) => ({
      key: t.type || t.key,
      title: t.title || TERMS_LABEL_BY_KEY[t.type] || t.type,
      version: t.version || '',
      effectiveDate: t.effective_date || '',
      content: t.content || '',
    }));

  const { consentedVersions, consentCompletedAt } = readConsentStateFromProfile(profile);

  const missingRequiredTerms = requiredTerms.filter((t) => {
    const recordedVersion = consentedVersions[t.key];
    if (!recordedVersion) return true;
    if (t.version && recordedVersion !== t.version) return true;
    return false;
  });

  return {
    requiredTerms,
    missingRequiredTerms,
    requiredConsentKeys: requiredKeys,
    requiresConsent: missingRequiredTerms.length > 0,
    consentCompletedAt,
  };
};

/**
 * 동의 저장
 * - terms_agreements_log 에 약관별 1문서씩 batch 생성 (본문 스냅샷 포함)
 * - users/{uid} 와 (있으면) partners/realtors/{uid} 의 동의 상태 필드 머지
 *
 * @param {object} params
 * @param {object} params.user - Firebase Auth user
 * @param {object} params.profile - users 문서 (또는 partners/realtors 머지본)
 * @param {string} params.role
 * @param {Array} params.terms - getConsentGateState().missingRequiredTerms 형태
 * @param {string} params.method - 'signup' | 'consent_gate' | 'admin'
 */
export const recordRequiredConsents = async ({
  user, profile, role, terms, method = 'consent_gate', path = '',
}) => {
  if (!user?.uid) throw new Error('로그인된 사용자 정보가 필요합니다.');
  if (!Array.isArray(terms) || terms.length === 0) return { saved: 0 };

  // ID 토큰 발급 → 서버 함수 (recordConsentWithIp) 로 위임
  // 서버단에서 IP·User-Agent 캡처 후 terms_agreements_log 에 batch 저장 + users/role 컬렉션 머지
  const idToken = await (auth.currentUser || user).getIdToken();
  const source = typeof window !== 'undefined' && window.ReactNativeWebView ? 'mobile_webview' : 'web';
  const finalPath = path || consumeConsentPath() || (method === 'consent_gate' ? 'consent-gate' : method);

  const res = await fetch(`${CF_BASE}/recordConsentWithIp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({
      profile: {
        uid: profile?.uid || user.uid,
        name: profile?.name || '',
        business_name: profile?.business_name || '',
        email: profile?.email || user.email || '',
        phone: profile?.phone || '',
        role: role || profile?.role || '',
      },
      role: role || profile?.role || '',
      terms,
      method,
      source,
      path: finalPath,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || '동의 저장 실패');
  }
  const data = await res.json();
  return { saved: data.saved || terms.length };
};

/**
 * 관리자: 동의 이력 조회 (최근순)
 * - 기본 50건. uid 또는 role로 필터 가능
 */
export const listAgreements = async ({ limit = 50, role, uid } = {}) => {
  const conditions = [];
  if (role) conditions.push(where('role', '==', role));
  if (uid) conditions.push(where('uid', '==', uid));
  const q = query(
    collection(db, LOG_COL),
    ...conditions,
    orderBy('createdAt', 'desc'),
    fsLimit(limit),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
